import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type { UIMessage } from '@tanstack/ai-client';
import { useAssistantChat } from '../hooks/use-assistant-chat';
import type {
  ProgressNote as ProgressNoteData,
  ToolStatus,
} from '../hooks/use-assistant-chat';
import { useAgentState } from '../hooks/use-agent-state';
import { useOpenRecord } from '../hooks/use-open-record';
import { useAssistantStore } from '../store/assistant-store';
import type { AgentState } from '../types/agent-state.types';
import type {
  PresentationBlock,
  TablePresentation,
} from '../types/presentation.types';
import { nearEdge } from '@utils/near-edge';
import { useFillViewport } from '@hooks/use-fill-viewport';
import { toolNarration } from '../helpers/tool-narration';
import { shouldShowDetailCard } from '../helpers/profile-card';
import { MessageBubble } from './message-bubble';
import { PresentationRenderer } from './presentation/presentation-renderer';
import { ToolProgress } from './tool-progress';
import { ConfirmationCard } from './confirmation-card';
import { ThinkingRow } from './thinking-row';
import { Composer } from './composer';
import { ContextChip } from './context-chip';
import { SuggestionChips } from './suggestion-chips';
import type { TiaReaction } from './tia/tia-avatar';
import { TiaThreadAvatar } from './tia/tia-thread-avatar';
import tiaRobot from '../assets/tia-robot.png';

function isVisiblePart(p: UIMessage['parts'][number]): boolean {
  return (p.type === 'text' && p.content.length > 0) || p.type === 'tool-call';
}

/** Assistant rows: Figma robot avatar beside prose (no bubble). */
function AssistantRow({ children }: { children: React.ReactNode }) {
  return (
    <div className='flex items-start gap-1'>
      <TiaThreadAvatar />
      <div className='min-w-0 flex-1 pt-1.5'>{children}</div>
    </div>
  );
}

/**
 * Which message renders the chip for each tool call.
 *
 * A confirmed write invokes the gateway TWICE on purpose — staged, then executed once
 * the user approves, with the resumed run replaying the node (see `tools_node`). Both
 * carry the same toolCallId, so without this the thread showed two identical
 * `create_task ✓` chips for one action.
 */
function toolChipOwners(messages: UIMessage[]): Map<string, string> {
  const owner = new Map<string, string>();
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === 'tool-call' && !owner.has(part.id)) {
        owner.set(part.id, message.id);
      }
    }
  }
  return owner;
}

/**
 * What the model said before calling a tool ("Let me pull the invoices…"): a
 * small grey line above that tool's chip. Progress, not the answer — it stays.
 */
function ProgressNote({ text }: { text: string }) {
  return (
    <p
      data-slot='progress-note'
      className='self-start text-xs text-gray-500'
    >
      {text}
    </p>
  );
}

/** One message rendered as its typed parts: tool chips + text bubbles, in stream order. */
function MessageParts({
  message,
  toolStatuses,
  chipOwners,
  progressNotes = {},
  reveal = false,
  onGrow,
  /** When true, skip assistant text — a detail_card owns that headline (Figma). */
  hideAssistantText = false,
}: {
  message: UIMessage;
  toolStatuses: Record<string, ToolStatus>;
  chipOwners: Map<string, string>;
  /** Narration by the toolCallId it introduces. */
  progressNotes?: Record<string, ProgressNoteData>;
  /** Type this message's answer out once (it arrived whole, after a check). */
  reveal?: boolean;
  /** Called as a typing answer grows, so the thread can follow it. */
  onGrow?: () => void;
  hideAssistantText?: boolean;
}) {
  const parts = message.parts.filter(isVisiblePart);
  if (parts.length === 0) return null; // e.g. the confirmation resume trigger
  return (
    <div className='flex flex-col gap-1.5'>
      {parts.map((p, i) => {
        if (p.type === 'tool-call') {
          if (chipOwners.get(p.id) !== message.id) return null;
          const status = toolStatuses[p.id] ?? 'running';
          // Denied / blocked internal steps look like errors to staff even
          // when the turn succeeded — hide them from the progress trace.
          if (status === 'denied') return null;
          const note = progressNotes[p.id]?.text;
          const chip = (
            <ToolProgress
              key={p.id}
              name={p.name}
              status={status}
            />
          );
          if (!note) return chip;
          return (
            <div
              key={p.id}
              className='flex flex-col gap-1'
            >
              <ProgressNote text={note} />
              {chip}
            </div>
          );
        }
        if (p.type === 'text') {
          if (message.role === 'user') {
            return (
              <MessageBubble
                key={`${message.id}-text-${i}`}
                role='user'
                content={p.content}
              />
            );
          }
          if (hideAssistantText) return null;
          return (
            <AssistantRow key={`${message.id}-text-${i}`}>
              <MessageBubble
                role='assistant'
                content={p.content}
                reveal={reveal}
                onGrow={onGrow}
              />
            </AssistantRow>
          );
        }
        return null;
      })}
    </div>
  );
}

/**
 * Notes whose tool call has no chip on screen (a hidden tool, or chips gone
 * after the transcript reloads), grouped by the user message of their turn.
 * They render as standalone lines under that message instead of vanishing.
 */
function orphanNotesByTurn(
  notes: Record<string, ProgressNoteData>,
  chipOwners: Map<string, string>
): Map<string, string[]> {
  const byTurn = new Map<string, string[]>();
  for (const [toolCallId, note] of Object.entries(notes)) {
    if (chipOwners.has(toolCallId) || !note.turnId) continue;
    byTurn.set(note.turnId, [...(byTurn.get(note.turnId) ?? []), note.text]);
  }
  return byTurn;
}

function assistantTextOf(message: UIMessage): string {
  return message.parts
    .filter(
      (p): p is Extract<UIMessage['parts'][number], { type: 'text' }> =>
        p.type === 'text' && p.content.length > 0
    )
    .map((p) => p.content)
    .join('\n')
    .trim();
}

function precedingUserText(
  messages: UIMessage[],
  messageIndex: number
): string | undefined {
  for (let i = messageIndex - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      return assistantTextOf(messages[i]);
    }
  }
  return undefined;
}

// `#` as a heading and list markers (`- `, `1. `) only disqualify at the very
// start of the line — elsewhere they're just punctuation a short status line
// might legitimately contain.
const MARKDOWN_HEADING_AT_START = /^#/;
const MARKDOWN_LIST_MARKER_AT_START = /^(?:[-*+]|\d+\.)\s/;
const MARKDOWN_LINK = /\[[^\]]*\]\([^)]*\)/;
const MARKDOWN_INLINE_SYNTAX = /[*_`]/;
const MARKDOWN_TABLE_PIPE = /\|/;

/**
 * Task 21: a detail_card's green headline is the assistant's own words ONLY
 * when they read like a short status line, not a formatted answer — at most
 * 80 chars after trimming, one line, and no markdown syntax. Exported so the
 * rule is independently testable; the long-answer path falls back to the
 * block's own `title` instead (see the detail_card render in MessageThread).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function isShortHeadline(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > 80) return false;
  if (trimmed.includes('\n')) return false;
  if (MARKDOWN_HEADING_AT_START.test(trimmed)) return false;
  if (MARKDOWN_LIST_MARKER_AT_START.test(trimmed)) return false;
  if (MARKDOWN_LINK.test(trimmed)) return false;
  if (MARKDOWN_INLINE_SYNTAX.test(trimmed)) return false;
  if (MARKDOWN_TABLE_PIPE.test(trimmed)) return false;
  return true;
}

export interface MessageThreadProps {
  /**
   * Reports the derived agent state up to the panel (the companion header's
   * avatar lives there, above this keyed component). Called with 'idle' on
   * unmount so the header settles when the thread is left.
   */
  onAgentStateChange?: (state: AgentState) => void;
  /**
   * FR-15: lifts the confirmation card's micro-reaction ('ack' on approve,
   * 'blink' on reject) up to the panel, which owns the header avatar.
   */
  onConfirmationReact?: (kind: TiaReaction) => void;
  /**
   * FR-11: lifts the narration phrase of the LATEST running tool (a tool-call
   * part with no end status yet) up to the panel for the header status line.
   * Called with null when nothing is running, and on unmount — the same lift
   * pattern as onAgentStateChange.
   */
  onNarrationChange?: (narration: string | null) => void;
  /**
   * Opens the left-of-panel table modal for a `kind: 'table'` presentation
   * (Figma View chip — tables are never inlined in the thread).
   */
  onOpenTable?: (block: TablePresentation) => void;
}

/**
 * FR-14: the text of the most recent user-typed message — what "Try again"
 * resends. Walks backwards past empty user messages (the confirmation resume
 * trigger carries no text); null when the thread has nothing to resend.
 */
function lastUserText(messages: UIMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'user') continue;
    const text = m.parts.find((p) => p.type === 'text' && p.content.length > 0);
    if (text && text.type === 'text') return text.content;
  }
  return null;
}

/** The latest tool-call part with no end status yet — the one still running. */
function latestRunningToolName(
  messages: UIMessage[],
  toolStatuses: Record<string, ToolStatus>
): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const parts = messages[i].parts;
    for (let j = parts.length - 1; j >= 0; j--) {
      const p = parts[j];
      if (p.type === 'tool-call' && !(p.id in toolStatuses)) return p.name;
    }
  }
  return null;
}

export function MessageThread({
  onAgentStateChange,
  onConfirmationReact,
  onNarrationChange,
  onOpenTable,
}: MessageThreadProps) {
  const {
    messages,
    isLoading,
    historyLoading,
    error,
    toolStatuses,
    runSignal,
    confirmation,
    unresolvedOperations,
    checkReceipt,
    presentationsByMessage,
    progressNotes,
    answerPhase,
    revealIds,
    loadOlder,
    hasOlder,
    loadingOlder,
    sendMessage,
    approve,
    approvalFailure,
    reject,
  } = useAssistantChat();
  const openRecord = useOpenRecord();
  // A WhatsApp conversation is answered on WhatsApp. The server refuses panel
  // messages and confirmations on it, so the panel offers neither.
  const readOnly = useAssistantStore((s) => s.activeSurface === 'WHATSAPP');
  const scrollRef = useRef<HTMLDivElement>(null);
  // scrollHeight captured just before an older page is requested. Set = the
  // next render is a PREPEND, so hold the reader's position instead of
  // scrolling to the bottom.
  const anchorRef = useRef<number | null>(null);
  // Whether the reader is following the tail. A stream re-renders on every
  // token, so pinning unconditionally would drag someone who scrolled up back
  // to the bottom several times a second — they could never read back while
  // the assistant is still talking. Starts true: a thread opens at its end.
  const pinnedRef = useRef(true);

  // A short transcript cannot be scrolled, so older pages would be stranded.
  // No anchoring here: with nothing to scroll, the layout effect below simply
  // pins to the bottom, which is where a chat belongs.
  useFillViewport(
    scrollRef,
    hasOlder,
    loadingOlder,
    loadOlder,
    messages.length
  );

  const requestOlder = () => {
    if (!hasOlder || loadingOlder) return;
    anchorRef.current = scrollRef.current?.scrollHeight ?? 0;
    loadOlder();
  };

  // Acting on the thread means you want to watch what happens, wherever you
  // had scrolled to.
  const send = (text: string, files?: File[]) => {
    pinnedRef.current = true;
    return files?.length ? sendMessage(text, files) : sendMessage(text);
  };

  // The chat hook must stay here (its SDK client is keyed per conversation
  // via this component's `key`), so the state is derived here and lifted.
  const agentState = useAgentState({
    runSignal,
    hasConfirmation: confirmation !== null,
    hasError: error !== undefined,
  });
  const reportStateRef = useRef(onAgentStateChange);
  reportStateRef.current = onAgentStateChange;
  useEffect(() => {
    reportStateRef.current?.(agentState);
  }, [agentState]);
  useEffect(() => () => reportStateRef.current?.('idle'), []);

  const chipOwners = useMemo(() => toolChipOwners(messages), [messages]);
  const orphanNotes = useMemo(
    () => orphanNotesByTurn(progressNotes, chipOwners),
    [progressNotes, chipOwners]
  );

  // A checked answer types out after it lands, with no new message for the
  // pin-to-bottom effect to react to: follow it on every step of the reveal,
  // unless the reader has scrolled away from the tail.
  const followTail = useCallback(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, []);

  // FR-11: narrate the latest running tool up to the panel (header status).
  const runningTool = latestRunningToolName(messages, toolStatuses);
  const narration = runningTool === null ? null : toolNarration(runningTool);
  const reportNarrationRef = useRef(onNarrationChange);
  reportNarrationRef.current = onNarrationChange;
  useEffect(() => {
    reportNarrationRef.current?.(narration);
  }, [narration]);
  useEffect(() => () => reportNarrationRef.current?.(null), []);

  // Layout effect: the correction must land before the browser paints, or the
  // reader sees the thread jump. Without it, prepending scrolls them back to
  // the top — straight into another fetch, and another, forever.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (anchorRef.current !== null) {
      el.scrollTop += el.scrollHeight - anchorRef.current;
      anchorRef.current = null;
      return;
    }
    if (!pinnedRef.current) return;
    // Jump, never animate. A smooth scroll from the top of a fresh thread
    // travels THROUGH the top edge, firing scroll events that read as "the
    // user scrolled up" — which paged in the entire transcript on open.
    el.scrollTop = el.scrollHeight;
  }, [messages, isLoading, confirmation]);

  // FR-14: what a "Try again" under an errored turn would resend.
  const retryText = error ? lastUserText(messages) : null;

  // Show the thinking row until the answer itself starts appearing — at which
  // point the growing bubble IS the indicator. Keyed on visible TEXT, not on
  // parts: a run that has fired a tool has parts, but a finished tool chip is
  // a record of something that already happened, not a sign of life. That gap
  // is where the panel used to sit silent for several seconds.
  const last = messages[messages.length - 1];
  const answering =
    last?.role === 'assistant' &&
    last.parts.some((p) => p.type === 'text' && p.content.length > 0);
  const waiting = isLoading && !answering;

  // Starter chips send a message, which a read-only conversation cannot take.
  const showStarters =
    messages.length === 0 && !isLoading && !historyLoading && !readOnly;

  // The header HOLDS `searching` for a whole multi-tool run so the avatar's eyes
  // don't strobe. The thread can be literal instead, because the chips above
  // already record which tools ran: a tool in flight narrates itself, and any
  // other moment — before the first call, between two calls, after the last one
  // — is the model reasoning. "Thinking…" is true in all three, which is why
  // this does not try to guess at "writing": tools here finish in well under a
  // second, and a run that looked finished often calls another one.
  const waitingState: AgentState =
    runningTool !== null ? 'searching' : 'thinking';

  return (
    <div className='flex h-full flex-col'>
      <div
        ref={scrollRef}
        data-slot='thread-scroll'
        className='flex-1 overflow-y-auto p-4'
        onScroll={(e) => {
          // Re-pin as soon as they come back to the tail, so the next reply
          // follows again without them having to do anything.
          pinnedRef.current = nearEdge(e.currentTarget, 'bottom', 60);
          if (nearEdge(e.currentTarget, 'top')) requestOlder();
        }}
      >
        <div className='flex flex-col gap-4'>
          {loadingOlder && (
            <p
              data-slot='thread-loading-older'
              className='text-center text-xs text-gray-400'
            >
              Loading earlier messages…
            </p>
          )}
          {showStarters && (
            <div
              data-slot='new-chat-empty'
              className='flex h-full flex-col items-center justify-center'
            >
              <img
                src={tiaRobot}
                alt=''
                width={117}
                height={160}
                data-slot='tia-mascot'
                className='h-[160px]! w-[117px]! object-contain'
              />{' '}
              <SuggestionChips
                messageCount={messages.length}
                busy={isLoading || historyLoading}
                onSelect={send}
              />
            </div>
          )}

          {messages.map((m, messageIndex) => {
            const userText = precedingUserText(messages, messageIndex);
            const blocks: PresentationBlock[] = (
              presentationsByMessage.get(m.id) ?? []
            ).filter((b) =>
              b.kind === 'detail_card'
                ? shouldShowDetailCard(b, userText)
                : true
            );
            const hasDetailCard = blocks.some((b) => b.kind === 'detail_card');
            // A detail_card only steals the bubble for a genuine short status
            // line (Figma green headline). A long/markdown answer stays the
            // normal bubble, and the card falls back to its own `title`.
            const useHeadline =
              hasDetailCard && isShortHeadline(assistantTextOf(m));
            const headline = useHeadline ? assistantTextOf(m) : undefined;
            return (
              <div
                key={m.id}
                className='flex flex-col gap-2'
              >
                <MessageParts
                  message={m}
                  toolStatuses={toolStatuses}
                  chipOwners={chipOwners}
                  progressNotes={progressNotes}
                  reveal={revealIds.has(m.id)}
                  onGrow={followTail}
                  hideAssistantText={useHeadline}
                />
                {(orphanNotes.get(m.id) ?? []).map((text, i) => (
                  <ProgressNote
                    key={`${m.id}-note-${i}`}
                    text={text}
                  />
                ))}
                {blocks.map((b, i) => {
                  const rendered = (
                    <PresentationRenderer
                      block={b}
                      onOpenRecord={openRecord}
                      onOpenTable={onOpenTable}
                      headline={b.kind === 'detail_card' ? headline : undefined}
                    />
                  );
                  // Align View chip under the prose column (avatar is ~32px).
                  if (b.kind === 'table') {
                    return (
                      <div
                        key={`${m.id}-block-${i}`}
                        className='flex items-start gap-1'
                      >
                        <div
                          className='w-8 shrink-0'
                          aria-hidden
                        />
                        {rendered}
                      </div>
                    );
                  }
                  return <div key={`${m.id}-block-${i}`}>{rendered}</div>;
                })}
              </div>
            );
          })}
          {waiting && (
            <ThinkingRow
              state={waitingState}
              narration={
                narration ??
                (answerPhase === 'checking' ? 'Checking the answer…' : null)
              }
            />
          )}
          {error && (
            <AssistantRow>
              <div className='flex flex-col gap-1.5'>
                <MessageBubble
                  role='assistant'
                  content={`⚠️ ${error.message}`}
                />
                {retryText !== null && (
                  <button
                    type='button'
                    data-slot='retry-chip'
                    onClick={() => send(retryText)}
                    className='self-start rounded-full border border-primary-200 bg-primary-50 px-3
                               py-1 text-xs text-primary-1000 transition-colors hover:bg-primary-100'
                  >
                    Try again
                  </button>
                )}
              </div>
            </AssistantRow>
          )}
          {(unresolvedOperations ?? []).map((operation) => (
            <div
              key={operation.id}
              role='status'
              className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm'
            >
              <p>
                {operation.status === 'PARTIAL'
                  ? 'Partially delivered.'
                  : 'Delivery is unresolved.'}{' '}
                Check the receipt before sending again.
              </p>
              {typeof operation.result?.unsent_body === 'string' &&
                operation.result.unsent_body && (
                  <p>Unsent text: {operation.result.unsent_body}</p>
                )}
              <button
                type='button'
                onClick={() => {
                  void checkReceipt(operation.id);
                }}
              >
                Check receipt
              </button>
            </div>
          ))}
          {confirmation && !readOnly && (
            <ConfirmationCard
              // One card per pending action: a refetch of the same action
              // keeps the user's ticks, a different action starts fresh
              // (recovery can swap drafts without passing through null).
              key={confirmation.draft.confirmation_token}
              draft={confirmation.draft}
              onApprove={(options) => {
                pinnedRef.current = true;
                return approve(options);
              }}
              onReject={reject}
              onReact={onConfirmationReact}
              // Only the refused draft's own card shows its refusal.
              approvalFailure={
                approvalFailure?.confirmationToken ===
                confirmation.draft.confirmation_token
                  ? approvalFailure
                  : null
              }
            />
          )}
        </div>
      </div>
      <ContextChip />
      <Composer
        onSend={send}
        busy={isLoading}
        readOnlyNote={
          readOnly ? 'This conversation continues on WhatsApp.' : undefined
        }
      />
    </div>
  );
}
